import { useState, useEffect } from "react";
import { FaCheck, FaEdit} from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExportOptions from "../ExportOptions";
import ConfirmActionModal from "../ConfirmDeleteModal";
import TableSkeleton from "../TableSkeleton";
import PageHeading from "../PageHeading";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const AssetModal = ({ isOpen, onClose, onSave, asset }) => {
  const [form, setForm] = useState({ assetName: "", quantity: "1", unitCost: "" });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (asset) {
        setForm({
          assetName: asset.assetName || "",
          quantity: asset.quantity != null ? String(asset.quantity) : "1",
          unitCost: asset.unitCost != null ? String(asset.unitCost) : "",
        });
      } else {
        setForm({ assetName: "", quantity: "1", unitCost: "" });
      }
      setErrors({});
    }
  }, [isOpen, asset]);

  if (!isOpen) return null;

  const qtyNum = parseInt(form.quantity) || 0;
  const costNum = parseFloat(form.unitCost) || 0;
  const totalCost = qtyNum * costNum;

  const validateForm = () => {
    const newErrors = {};
    if (!form.assetName.trim()) newErrors.assetName = "Asset Name is required";
    else if (form.assetName.trim().length < 3) newErrors.assetName = "Asset Name must be at least 3 characters";
    if (!form.quantity || qtyNum < 1) newErrors.quantity = "Quantity must be at least 1";
    if (form.unitCost === "" || costNum < 0) newErrors.unitCost = "Enter a valid unit cost";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors in the form", { position: "top-right", autoClose: 3000 });
      return false;
    }
    return true;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const e = { ...prev };
      delete e[field];
      return e;
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const payload = {
        assetName: form.assetName.trim(),
        quantity: qtyNum,
        unitCost: costNum,
      };

      if (asset) {
        const response = await fetch(`https://api.muktihospital.com/api/assets/edit/${asset.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "x-api-key": API_KEY },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to update asset");
        }
        const { asset: updatedAsset } = await response.json();
        onSave(asset.id, updatedAsset);
        toast.success("Asset updated successfully!", { position: "top-right", autoClose: 3000 });
        onClose();
      } else {
        const response = await fetch("https://api.muktihospital.com/api/assets/add", {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-api-key": API_KEY },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to add asset");
        }
        const { asset: newAsset } = await response.json();
        onSave(null, [newAsset]);
        toast.success("Asset added successfully!", { position: "top-right", autoClose: 3000 });
        onClose();
      }
    } catch (error) {
      console.error("Error saving asset:", error);
      toast.error(error.message || "Error saving asset!", { position: "top-right", autoClose: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden p-2">
      <div className="bg-white rounded-lg shadow-lg w-96">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-700">{asset ? "Edit Asset" : "Add Asset"}</h2>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Asset Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.assetName}
              onChange={(e) => handleChange("assetName", e.target.value)}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${errors.assetName ? "border-red-500" : ""}`}
              placeholder="Asset Name"
              disabled={isLoading}
            />
            {errors.assetName && <span className="text-red-500 text-xs mt-1">{errors.assetName}</span>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                value={form.quantity}
                onChange={(e) => handleChange("quantity", e.target.value)}
                className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${errors.quantity ? "border-red-500" : ""}`}
                placeholder="1"
                disabled={isLoading}
              />
              {errors.quantity && <span className="text-red-500 text-xs mt-1">{errors.quantity}</span>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit Cost <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.unitCost}
                onChange={(e) => handleChange("unitCost", e.target.value)}
                className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${errors.unitCost ? "border-red-500" : ""}`}
                placeholder="0.00"
                disabled={isLoading}
              />
              {errors.unitCost && <span className="text-red-500 text-xs mt-1">{errors.unitCost}</span>}
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Total Cost</span>
            <span className="text-lg font-bold text-M-heading-color">৳ {totalCost.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const StoreAsset = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetToEdit, setAssetToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const itemsPerPage = 10;

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await fetch("https://api.muktihospital.com/api/assets", {
        method: "GET",
        headers: { "Content-Type": "application/json", "x-api-key": API_KEY },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch assets");
      }
      const { assets } = await response.json();
      const formattedAssets = assets.map((asset) => ({
        id: asset.id,
        assetName: asset.assetName,
        quantity: asset.quantity,
        unitCost: asset.unitCost,
        totalCost: asset.totalCost,
      }));
      setAssets(formattedAssets);
    } catch (error) {
      console.error("Error fetching assets:", error);
      toast.error(error.message || "Error fetching assets!", { position: "top-right", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  const filteredAssets = assets.filter((asset) =>
    asset.assetName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);

  const currentData = filteredAssets
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((asset, index) => ({
      ...asset,
      displayId: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedRows(currentData.map((asset) => asset.id));
    else setSelectedRows([]);
  };

  const handleRowSelect = (assetId) => {
    setSelectedRows((prev) =>
      prev.includes(assetId) ? prev.filter((id) => id !== assetId) : [...prev, assetId]
    );
  };

  const handleDelete = async (assetIds) => {
    const idsToDelete = Array.isArray(assetIds) ? assetIds : [assetIds];
    setLoading(true);
    try {
      for (const id of idsToDelete) {
        const response = await fetch(`https://api.muktihospital.com/api/assets/delete/${id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "x-api-key": API_KEY },
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to delete asset with ID ${id}`);
        }
      }
      await fetchAssets();
      setSelectedRows([]);
      toast.success(`${idsToDelete.length} asset(s) deleted successfully!`, { position: "top-right", autoClose: 3000 });
    } catch (error) {
      console.error("Error deleting assets:", error);
      toast.error(error.message || "Error deleting asset(s)!", { position: "top-right", autoClose: 3000 });
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
    }
  };

  const handleSaveAsset = async (id, data) => {
    // Both add and edit just refetch to stay in sync with the backend
    await fetchAssets();
  };

  const handleSelect = (asset) => {
    setAssetToEdit(asset);
    setAssetModalOpen(true);
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Store Management" },
    { label: "Store Asset" },
  ];

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["ID", "Asset Name", "Quantity", "Unit Cost", "Total Cost"];
  const dataMapper = (item, index) =>
    [
      item.displayId || (index !== undefined ? (currentPage - 1) * itemsPerPage + index + 1 : item.id),
      item.assetName || "",
      item.quantity ?? "",
      item.unitCost ?? "",
      item.totalCost ?? "",
    ].join("\t");

  const skeletonColumns = [
    { width: "40px", height: "16px", className: "text-left print-hidden-in-print", skeletonClassName: "my-5 mx-3" },
    { width: "60px", height: "16px", className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3", skeletonClassName: "" },
    { width: "160px", height: "16px", className: "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3", skeletonClassName: "" },
    { width: "60px", height: "16px", className: "text-right font-inter font-normal text-sm text-M-heading-color py-5 px-3", skeletonClassName: "ml-auto" },
    { width: "80px", height: "16px", className: "text-right font-inter font-normal text-sm text-M-heading-color py-5 px-3", skeletonClassName: "ml-auto" },
    { width: "90px", height: "16px", className: "text-right font-inter font-normal text-sm text-M-heading-color py-5 px-3", skeletonClassName: "ml-auto" },
    { width: "80px", height: "16px", className: "text-right p-3 print-hidden-in-print", skeletonClassName: "ml-auto" },
  ];

  return (
    <div>
      <div className="print-hidden">
        <PageHeading title="Store Asset" breadcrumbs={breadcrumbs} />
      </div>
      <ToastContainer />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">Asset List</h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by asset name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="flex items-center w-full justify-between gap-3">
              <button
                onClick={() => {
                  setAssetToEdit(null);
                  setAssetModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Asset
              </button>
              <ExportOptions
                data={currentData}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Asset List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="overflow-x-auto print-wrapper">
            {loading ? (
              <div className="print-hidden">
                <table className="min-w-[760px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">ID</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Asset Name</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Quantity</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Unit Cost</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Total Cost</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} baseColor="#e5e7eb" highlightColor="#f3f4f6" />
                  </tbody>
                </table>
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="p-5 text-center text-gray-600 print-hidden">No assets found.</div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[760px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="agreement"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={currentData.length > 0 && currentData.every((item) => selectedRows.includes(item.id))}
                            disabled={loading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label htmlFor="agreement" className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">ID</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Asset Name</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Quantity</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Unit Cost</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3">Total Cost</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((asset, index) => (
                      <tr key={asset.id} className="border-b hover:bg-gray-50">
                        <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                          <div className="relative left-3 -top-[3px]">
                            <input
                              type="checkbox"
                              id={`asset-${index}`}
                              className="hidden peer print-hidden"
                              checked={selectedRows.includes(asset.id)}
                              onChange={() => handleRowSelect(asset.id)}
                              disabled={loading}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label htmlFor={`asset-${index}`} className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"></label>
                          </div>
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">{asset.displayId}</td>
                        <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{asset.assetName}</td>
                        <td className="text-right font-inter font-normal text-sm text-M-heading-color py-5 px-3">{asset.quantity ?? "-"}</td>
                        <td className="text-right font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {asset.unitCost != null ? `৳ ${Number(asset.unitCost).toFixed(2)}` : "-"}
                        </td>
                        <td className="text-right font-inter font-semibold text-sm text-M-heading-color py-5 px-3">
                          {asset.totalCost != null ? `৳ ${Number(asset.totalCost).toFixed(2)}` : "-"}
                        </td>
                        <td className="p-3 text-right space-x-2 print-hidden-in-print">
                          <button
                            onClick={() => handleSelect(asset)}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            disabled={loading}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRows([asset.id]);
                              setDeleteModalOpen(true);
                            }}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            disabled={loading}
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                  Records: {currentPage * itemsPerPage - itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, filteredAssets.length)} of {filteredAssets.length}
                </p>
                <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                  <li>
                    <button className="table-pagination-item disabled:opacity-50" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                      <MdKeyboardArrowLeft />
                    </button>
                  </li>
                  {totalPages <= 10
                    ? [...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${currentPage === i + 1 ? "bg-M-primary-color text-white" : ""}`}
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))
                    : (() => {
                        const pages = [];
                        const firstPages = 5;
                        const lastPages = 3;
                        for (let i = 1; i <= Math.min(firstPages, totalPages); i++) {
                          pages.push(
                            <li key={i}>
                              <button className={`table-pagination-item ${currentPage === i ? "bg-M-primary-color text-white" : ""}`} onClick={() => handlePageChange(i)}>
                                {i}
                              </button>
                            </li>
                          );
                        }
                        if (totalPages > firstPages + lastPages) {
                          pages.push(<li key="ellipsis" className="table-pagination-item">...</li>);
                        }
                        for (let i = Math.max(firstPages + 1, totalPages - lastPages + 1); i <= totalPages; i++) {
                          pages.push(
                            <li key={i}>
                              <button className={`table-pagination-item ${currentPage === i ? "bg-M-primary-color text-white" : ""}`} onClick={() => handlePageChange(i)}>
                                {i}
                              </button>
                            </li>
                          );
                        }
                        return pages;
                      })()}
                  <li>
                    <button className="table-pagination-item" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                      <MdKeyboardArrowRight />
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="asset"
        title="Confirm Delete"
        message={
          Array.isArray(selectedRows) && selectedRows.length > 1
            ? `Do you really want to delete ${selectedRows.length} asset(s)?`
            : `Do you really want to delete this asset?`
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <AssetModal
        isOpen={assetModalOpen}
        onClose={() => {
          setAssetToEdit(null);
          setAssetModalOpen(false);
        }}
        onSave={handleSaveAsset}
        asset={assetToEdit}
      />
    </div>
  );
};

export default StoreAsset;